/**
 * Financial Accounts Form Section
 * Manages bank accounts and assets
 */

import * as React from "react";
import {
  Add01Icon,
  CreditCardIcon,
  Delete03Icon,
  FinancialAccountFields,
  FormTable,
  FormTableAddButton,
  FormTableBody,
  useContainerRadiusSync,
  useFormTableAddButtonState,
  Button,
} from "@porchpass/ui";
import type { DomainAssetType } from "@porchpass/api-client";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import type { FinancialAccount } from "../types/applicationTypes";
import { ACCOUNT_TYPES } from "src/constants/enumOptions";
import { validateFinancialAccountField } from "../utils/validationSchemas";

interface FinancialAccountsFormProps {
  isCoBorrower?: boolean;
}

const createEmptyAccount = (): FinancialAccount => ({
  id: crypto.randomUUID(),
  accountType: "" as DomainAssetType,
  institutionName: "",
  accountNumber: "",
  balance: undefined,
});

export const FinancialAccountsForm: React.FC<FinancialAccountsFormProps> = ({
  isCoBorrower = false,
}) => {
  const store = useApplicationFormStore();
  const borrower = isCoBorrower
    ? store.data.coBorrower
    : store.data.primaryBorrower;
  const borrowerType = isCoBorrower ? "co" : "primary";
  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";
  const addButtonState = useFormTableAddButtonState();
  const containerStyles = useContainerRadiusSync({
    adjacentToBottom: true,
    bottomExpanded: addButtonState.isExpanded,
  });

  const [accountForms, setAccountForms] = React.useState<FinancialAccount[]>(
    [],
  );

  const accounts = borrower?.financialAccounts || [];

  React.useEffect(() => {
    if (accounts.length > 0 && accountForms.length === 0) {
      setAccountForms([...accounts]);
    }
  }, [accounts.length]);

  if (!borrower) return null;

  const getErrors = (index: number): Record<string, string> => {
    const prefix = `${borrowerPrefix}.financialAccounts.${index}.`;
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(store.fieldErrors)) {
      if (k.startsWith(prefix)) {
        result[k.slice(prefix.length)] = v;
      }
    }
    return result;
  };

  const handleAddAccount = () => {
    const newAccount = createEmptyAccount();
    store.addFinancialAccount(borrowerType, newAccount);
    setAccountForms([...accountForms, newAccount]);
  };

  const updateAccountField = <K extends keyof FinancialAccount>(
    index: number,
    field: K,
    value: FinancialAccount[K],
  ) => {
    if (typeof value === "string" || typeof value === "number") {
      const errorKey = `${borrowerPrefix}.financialAccounts.${index}.${String(field)}`;
      if (
        store.fieldErrors[errorKey] &&
        !validateFinancialAccountField(String(field), String(value))
      ) {
        store.clearFieldError(errorKey);
      }
    }
    const updated = { ...accountForms[index], [field]: value };
    const newForms = [...accountForms];
    newForms[index] = updated;
    setAccountForms(newForms);
    store.updateFinancialAccount(borrowerType, index, updated);
  };

  const handleDeleteAccount = (index: number) => {
    store.removeFinancialAccount(borrowerType, index);
    setAccountForms(accountForms.filter((_, i) => i !== index));
  };

  const renderAccountForm = (account: FinancialAccount, index: number) => (
    <FormTableBody key={index}>
      <div className="flex justify-between items-center mb-4">
        <p className="text-base font-medium text-[#111827]">Account Details</p>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => handleDeleteAccount(index)}
        >
          <Delete03Icon size={16} className="text-red-500" />
        </Button>
      </div>

      <FinancialAccountFields
        account={account}
        errors={getErrors(index)}
        accountTypeOptions={ACCOUNT_TYPES}
        onFieldChange={(field, value) =>
          updateAccountField(index, field, value)
        }
      />
    </FormTableBody>
  );

  return (
    <FormTable
      title="Financial Accounts"
      headerIcon={
        <CreditCardIcon
          variant="bulk-rounded"
          size={16}
          className="text-primary"
        />
      }
      style={containerStyles.style}
    >
      {accountForms.map((account, index) => renderAccountForm(account, index))}

      <FormTableAddButton
        onClick={handleAddAccount}
        {...addButtonState.interactionProps}
      >
        <Add01Icon variant="stroke-rounded" size={12} />
        {accountForms.length === 0 ? "Add account" : "Additional account"}
      </FormTableAddButton>
    </FormTable>
  );
};

export default FinancialAccountsForm;
